/**
 * web-studio-sites D4 — reading a custom page's placeholders:
 * `<div data-telify="<piece>" data-limit="4" data-show-times="true"></div>`.
 * The options are the placeholder's other `data-` attributes, named as the
 * contract names them (`data-show-times` is `showTimes`); the piece checks them
 * against its contract, so an unknown or invalid one warns and keeps its default.
 */
import type { OptionSpec, PieceContract } from '../contract/define';

/** A placeholder: an element naming a piece that is not itself a drawn piece's root, outside the kit's own host. */
const PLACEHOLDER = '[data-telify]:not([data-telify-root])';

/** The element the kit renders its shared parts into (the messages panel and banner); never scanned. */
export const KIT_HOST_ATTRIBUTE = 'data-telify-kit';

export function findPlaceholders(scope: ParentNode): HTMLElement[] {
  return [...scope.querySelectorAll<HTMLElement>(PLACEHOLDER)].filter((el) => !el.closest(`[${KIT_HOST_ATTRIBUTE}]`));
}

/** An attribute's text as the option's type; a value that does not parse is passed on, for the contract to refuse. */
function parseOption(spec: OptionSpec | undefined, text: string): unknown {
  switch (spec?.type) {
    case 'boolean':
      return text === 'true' ? true : text === 'false' ? false : text;
    case 'number':
      return text.trim() === '' ? text : Number(text);
    default:
      return text;
  }
}

/** The placeholder's raw options for its piece. */
export function placeholderOptions(el: HTMLElement, contract: PieceContract): Record<string, unknown> {
  const raw: Record<string, unknown> = {};
  for (const [key, text] of Object.entries(el.dataset)) {
    if (key.startsWith('telify') || text === undefined) continue;
    raw[key] = parseOption(contract.options[key], text);
  }
  // D15: a placed piece shows no label above its heading unless the page sets one.
  if ('eyebrow' in contract.options && !('eyebrow' in raw)) raw.eyebrow = null;
  return raw;
}
